import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { decodeShare } from "../lib/share";
import { fmtUSD, fmtMonthLabel } from "../lib/format";
import { REGISTER_URL } from "../lib/config";
import { useTheme } from "../lib/theme";
import GrowthChart from "../components/GrowthChart";
import { Logo } from "../components/Logo";
import { ExternalLink, TrendingUp, Wallet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadInitial() {
  const params = new URLSearchParams(window.location.search);
  const s = params.get("s");
  if (s) {
    const decoded = decodeShare(s);
    if (decoded) return decoded;
  }
  const arr = Array.from({ length: 120 }, () => ({
    deposit: 0,
    withdrawal_value: 0,
    withdrawal_type: "usd",
    withdrawal_mode: "none",
  }));
  arr[0] = { ...arr[0], deposit: 1000 };
  return { startYear: 2026, startMonth: 9, inputs: arr };
}

export default function Embed() {
  const [state, setState] = useState(loadInitial);
  const [sim, setSim] = useState(null);
  const { setTheme } = useTheme();
  const params = new URLSearchParams(window.location.search);
  const compact = params.get("compact") === "1";
  const showChart = params.get("chart") !== "0";
  const showTable = params.get("table") !== "0" && !compact;

  // Force theme on embed via URL param
  useEffect(() => {
    const theme = params.get("theme") || "dark";
    setTheme(theme);
    document.body.classList.add("wc-embed-body");
    return () => document.body.classList.remove("wc-embed-body");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payload = useMemo(() => {
    const inputs = state.inputs
      .map((row, idx) => {
        const dep = Number(row.deposit) || 0;
        const wv = Number(row.withdrawal_value) || 0;
        if (dep <= 0 && wv <= 0 && row.withdrawal_mode === "none") return null;
        return {
          month_index: idx,
          deposit: dep,
          withdrawal_value: wv,
          withdrawal_type: row.withdrawal_type || "usd",
          withdrawal_mode: row.withdrawal_mode || "none",
        };
      })
      .filter(Boolean);
    return {
      start_year: state.startYear,
      start_month: state.startMonth,
      months: 120,
      inputs,
    };
  }, [state]);

  const run = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/simulate`, payload);
      setSim(data);
    } catch (e) { console.error(e); }
  }, [payload]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="wc-embed" data-testid="embed-page">
      <div className="wc-embed-head">
        <div className="wc-embed-brand">
          <Logo size={32} />
          <div>
            <div className="wc-embed-brand-name">WordCripto</div>
            <div className="wc-embed-brand-sub">Simulazione 10 anni · 0,17%/giorno</div>
          </div>
        </div>
        <a
          className="wc-embed-cta"
          href={REGISTER_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="embed-register"
        >
          Iscriviti ora <ExternalLink size={14} />
        </a>
      </div>

      {sim && (
        <>
          <div className="wc-embed-kpis">
            <div className="wc-embed-kpi">
              <span><Wallet size={12} /> Versato</span>
              <b>{fmtUSD(sim.total_deposited, "it")}</b>
            </div>
            <div className="wc-embed-kpi">
              <span>Ritirato</span>
              <b>{fmtUSD(sim.total_withdrawn, "it")}</b>
            </div>
            <div className="wc-embed-kpi wc-embed-kpi-strong">
              <span><TrendingUp size={12} /> Totale finale</span>
              <b>{fmtUSD(sim.final_total, "it")}</b>
            </div>
            <div className="wc-embed-kpi wc-embed-kpi-good">
              <span>Crescita</span>
              <b>{fmtUSD(sim.total_growth, "it")}</b>
            </div>
          </div>

          {showChart && (
            <div className="wc-embed-chart">
              <GrowthChart rows={sim.rows} height={compact ? 220 : 320} />
            </div>
          )}

          {showTable && (
            <div className="wc-embed-table-wrap">
              <table className="wc-table wc-embed-table">
                <thead>
                  <tr>
                    <th>Mese</th>
                    <th style={{ textAlign: "right" }}>Capitale</th>
                    <th style={{ textAlign: "right" }}>Guadagno</th>
                    <th style={{ textAlign: "right" }}>Liquidità</th>
                  </tr>
                </thead>
                <tbody>
                  {sim.rows.filter((_, i) => i % 12 === 11 || sim.rows[i].is_year_start).map((row, i) => (
                    <tr key={i} className={row.is_year_start ? "wc-tr-year" : ""}>
                      <td className="wc-td-month">{fmtMonthLabel(row, "it")}</td>
                      <td className="wc-td-num wc-td-strong">{fmtUSD(row.capital_end, "it")}</td>
                      <td className="wc-td-num wc-td-profit">{fmtUSD(row.month_profit, "it")}</td>
                      <td className="wc-td-num wc-td-muted">{fmtUSD(row.liquidity_end, "it")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="wc-embed-footer">
        <a href={REGISTER_URL} target="_blank" rel="noreferrer" className="wc-embed-footer-cta">
          Vuoi risultati simili? <b>Iscriviti su wordcripto.com →</b>
        </a>
      </div>
    </div>
  );
}
